import { createSlice, createSelector } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './store';

export interface CartItem {
    productId: string;
    name: string;
    brand: string;
    price: number;
    image: string;
    category: string;
    qty: number;
}

interface CartState {
    items: CartItem[];
}

function loadFromStorage(): CartItem[] {
    try {
        const raw = localStorage.getItem('cart');
        return raw ? (JSON.parse(raw) as CartItem[]) : [];
    } catch {
        return [];
    }
}

function saveToStorage(items: CartItem[]) {
    localStorage.setItem('cart', JSON.stringify(items));
}

const initialState: CartState = {
    items: loadFromStorage(),
};

const cartSlice = createSlice({
    name: 'cart',
    initialState,
    reducers: {
        addToCart(state, action: PayloadAction<Omit<CartItem, 'qty'>>) {
            const existing = state.items.find(i => i.productId === action.payload.productId);
            if (existing) {
                existing.qty += 1;
            } else {
                state.items.push({ ...action.payload, qty: 1 });
            }
            saveToStorage(state.items);
        },
        removeFromCart(state, action: PayloadAction<string>) {
            state.items = state.items.filter(i => i.productId !== action.payload);
            saveToStorage(state.items);
        },
        updateQty(state, action: PayloadAction<{ productId: string; qty: number }>) {
            const { productId, qty } = action.payload;
            if (qty <= 0) {
                state.items = state.items.filter(i => i.productId !== productId);
            } else {
                const item = state.items.find(i => i.productId === productId);
                if (item) item.qty = qty;
            }
            saveToStorage(state.items);
        },
        clearCart(state) {
            state.items = [];
            localStorage.removeItem('cart');
        },
    },
});

export const { addToCart, removeFromCart, updateQty, clearCart } = cartSlice.actions;
export default cartSlice.reducer;

export const selectCartCount = createSelector(
    (state: RootState) => state.cart.items,
    items => items.reduce((sum, i) => sum + i.qty, 0)
);
